/**
 * PIFster Turnstile Gate
 *
 * Strict TypeScript conversion for Cloudflare Turnstile rendering and token sync.
 */

type PifsterWindow = Window & {
  PIFSTER_TURNSTILE?: TurnstileConfig | null;
  turnstile?: TurnstileApi;
};

interface TurnstileConfig {
  readonly siteKey: string;
}

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions
  ) => void;
}

interface TurnstileRenderOptions {
  readonly sitekey: string;
  readonly callback: (token: string) => void;
  readonly 'expired-callback': () => void;
  readonly 'error-callback': () => void;
}

interface TurnstileContainer extends HTMLElement {
  dataset: DOMStringMap & {
    turnstileRendered?: string;
    turnstileToken?: string;
  };
}

const turnstileWindow = window as PifsterWindow;
const turnstileConfig = turnstileWindow.PIFSTER_TURNSTILE ?? null;
if (!turnstileConfig?.siteKey) {
  // No-op: gate is disabled when the site key is absent.
} else {
  const turnstileSiteKey: string = turnstileConfig.siteKey;

  function qs(selector: string, root: ParentNode = document): Element | null {
    return root.querySelector(selector);
  }

  function qsa(selector: string, root: ParentNode = document): NodeListOf<Element> {
    return root.querySelectorAll(selector);
  }

  function ensureTurnstileScriptLoaded(): Promise<void> {
    return new Promise((resolve: () => void, reject: (reason: Error) => void) => {
      if (turnstileWindow.turnstile && typeof turnstileWindow.turnstile.render === 'function') {
        resolve();
        return;
      }

      const existing = qs('script[data-pifster-turnstile="1"]');
      if (existing) {
        existing.addEventListener('load', () => {
          resolve();
        });
        existing.addEventListener('error', () => {
          reject(new Error('Failed to load Turnstile script'));
        });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.setAttribute('data-pifster-turnstile', '1');
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Turnstile script'));
      };
      document.head.appendChild(script);
    });
  }

  function ensureTokenInput(container: HTMLElement): HTMLInputElement | null {
    const form = container.closest('form');
    if (!form) {
      return null;
    }

    let input = form.querySelector<HTMLInputElement>('input[name="turnstile_token"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'turnstile_token';
      form.appendChild(input);
    }

    return input;
  }

  function setToken(container: TurnstileContainer, token: string): void {
    container.dataset.turnstileToken = String(token ?? '');

    const input = ensureTokenInput(container);
    if (!input) {
      return;
    }

    input.value = String(token ?? '');
  }

  function renderWidget(container: TurnstileContainer): void {
    if (container.dataset.turnstileRendered === '1') {
      return;
    }

    container.dataset.turnstileRendered = '1';

    ensureTurnstileScriptLoaded()
      .then(() => {
        if (!turnstileWindow.turnstile || typeof turnstileWindow.turnstile.render !== 'function') {
          return;
        }

        turnstileWindow.turnstile.render(container, {
          sitekey: turnstileSiteKey,
          callback: (token: string) => {
            setToken(container, token);
          },
          'expired-callback': () => {
            setToken(container, '');
          },
          'error-callback': () => {
            setToken(container, '');
          },
        });
      })
      .catch(() => {
        container.dataset.turnstileRendered = '0';
      });
  }

  function scan(root: ParentNode): void {
    const containers = qsa('.pifster-turnstile', root);
    if (containers.length === 0) {
      return;
    }

    containers.forEach((node: Element) => {
      renderWidget(node as TurnstileContainer);
    });
  }

  function observe(): void {
    if (typeof MutationObserver !== 'function') {
      return;
    }

    const observer = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        mutation.addedNodes.forEach((node: Node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.classList.contains('pifster-turnstile')) {
            renderWidget(node as TurnstileContainer);
          }

          scan(node);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Listen for custom event from vote-intent-controller to trigger re-render
    document.addEventListener('pifster:turnstile-rerender', (event: Event) => {
      if (event.target instanceof HTMLElement && event.target.classList.contains('pifster-turnstile')) {
        renderWidget(event.target as TurnstileContainer);
      }
    });
  }

  function initTurnstileGate(): void {
    scan(document);
    observe();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTurnstileGate);
  } else {
    initTurnstileGate();
  }
}

export {};
